import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { PrismaEnums } from '@airreading/schema/browser';
import { CSVUploadResponse } from '../csv.js';
import { classTimeTypeSchema } from './class-time-shared.js';
import { fullClassTimeSchema } from './class-time.js';
import { learningPathWithLessonsSchema, studentGroupTypeSchema } from './learning-path.js';
import { lessonSchema } from './lesson.js';
import { coachViewableStudentSchema, studentYearNullableSchema } from './student.js';
import { userLikeSchema, userWithCoachProfileSchema } from './user.js';
import { paginatedResponse } from './util-schema-types.js';

import {
  DEPRECATED_pagingSchemaWithMaxPageSize,
  filteringSchemaFrom,
  pagingSchema,
  sortingSchemaFrom,
  sortOrderSchemaFrom,
} from './modifier.js';
import { coachSessionRemarkV2Schema, sessionStatusSchema } from './session-shared.js';
import {
  AttendanceStatus,
  AttendanceStatusLabelMap,
  attendanceStatusSchema,
  AttendanceStatusSelectable,
  attendanceStatusSelectableSchema,
  attendanceStatusSelectableValues,
  CoachAttendanceLateTagRegex,
  CoachAttendanceRollupItem,
  coachAttendanceRollupItemSchema,
  CoachAttendanceStatus,
  CoachAttendanceStatusLabelMap,
  coachAttendanceStatusSchema,
  CoachAttendanceTag,
  CoachAttendanceTagLabelMap,
  coachAttendanceTagSchema,
  EmailsSent,
  EmailsSentLabelMap,
  emailsSentSchema,
  GetCoachAttendanceRollupResult,
  getCoachAttendanceRollupResultSchema,
  STUDENT_GROUP_NOTES_MAX_LENGTH,
  StudentGroup,
  studentGroupSchema,
  StudentGroupStatus,
  StudentGroupStatusLabelMap,
  studentGroupStatusSchema,
  studentGroupWithClassTimeSchema,
} from './student-group-shared.js';
export {
  AttendanceStatus,
  AttendanceStatusLabelMap,
  attendanceStatusSchema,
  attendanceStatusSelectableSchema,
  attendanceStatusSelectableValues,
  CoachAttendanceLateTagRegex,
  coachAttendanceRollupItemSchema,
  CoachAttendanceStatus,
  CoachAttendanceStatusLabelMap,
  coachAttendanceStatusSchema,
  CoachAttendanceTagLabelMap,
  coachAttendanceTagSchema,
  EmailsSent,
  EmailsSentLabelMap,
  emailsSentSchema,
  getCoachAttendanceRollupResultSchema,
  STUDENT_GROUP_NOTES_MAX_LENGTH,
  studentGroupSchema,
  StudentGroupStatus,
  StudentGroupStatusLabelMap,
  studentGroupStatusSchema,
  studentGroupWithClassTimeSchema,
};
export type {
  AttendanceStatusSelectable,
  CoachAttendanceRollupItem,
  CoachAttendanceTag,
  GetCoachAttendanceRollupResult,
  StudentGroup,
};

export type OneTimeConflictSummary = z.infer<typeof oneTimeConflictSummarySchema>;
export const oneTimeConflictSummarySchema = z.object({
  sessionId: z.string(),
  startInstant: z.instanceof(Temporal.Instant),
  endInstant: z.instanceof(Temporal.Instant),
  classDate: z.string(),
  classTime: z.string(),
  schoolName: z.string(),
  districtName: z.string(),
});

/** Minute values as strings for one-time class override duration selects. */
export const oneTimeClassDurationOptions = ['30', '40'] as const;

export const oneTimeClassDurationLabels: Record<string, string> = {
  '30': '30 minutes',
  '40': '40 minutes',
};

export const studentGroupWithNamesSchema = studentGroupSchema.extend({
  studentNames: z.array(z.string()).optional(),
});
export type StudentGroupWithNames = z.infer<typeof studentGroupWithNamesSchema>;

export const levelChangeWarningSchema = z.object({
  learningPathName: z.string().optional(),
  startingLessonName: z.string(),
  note: z.string(),
});
export type LevelChangeWarning = z.infer<typeof levelChangeWarningSchema>;

export const attendancePolicyV2AllowanceSchema = z
  .object({
    policyVersion: z.nativeEnum(PrismaEnums.TeacherAttendancePolicyVersion),
    eligibleSessionCount: z.number().int().nonnegative(),
    totalSubRequestAllowance: z.number().int().nonnegative(),
    shortNoticeSubRequestAllowance: z.number().int().nonnegative(),
    totalSubRequestUsage: z.number().int().nonnegative(),
    shortNoticeSubRequestUsage: z.number().int().nonnegative(),
    triggeringDimensions: z.array(z.nativeEnum(PrismaEnums.TeacherAttendancePolicyTriggerDimension)),
    usageEvents: z.array(
      z.object({
        sessionId: z.string(),
        eventPlainDate: z.instanceof(Temporal.PlainDate),
        kind: z.nativeEnum(PrismaEnums.TeacherAttendancePolicyUsageKind),
      }),
    ),
    calculatedAt: z.instanceof(Temporal.Instant),
    actionLevel: z.nativeEnum(PrismaEnums.TeacherAttendancePolicyActionLevel),
    noShowCount: z.number().int().nonnegative(),
    lateEntryCount: z.number().int().nonnegative(),
    earlyExitCount: z.number().int().nonnegative(),
    final14DayProtectionActive: z.boolean(),
  })
  .refine(
    ({ shortNoticeSubRequestAllowance, totalSubRequestAllowance }) =>
      shortNoticeSubRequestAllowance <= totalSubRequestAllowance,
    {
      message: 'Short-notice sub-request allowance cannot exceed the total sub-request allowance',
      path: ['shortNoticeSubRequestAllowance'],
    },
  );
export type AttendancePolicyV2Allowance = z.infer<typeof attendancePolicyV2AllowanceSchema>;

export const attendancePolicyV2HistoryEventKindSchema = z.enum([
  'no_show',
  'late_entry',
  'early_exit',
  'regular_sub_request',
  'short_notice_sub_request',
  'exemption',
  'technical_failure',
]);
export type AttendancePolicyV2HistoryEventKind = z.infer<typeof attendancePolicyV2HistoryEventKindSchema>;
export const AttendancePolicyV2HistoryEventKind = attendancePolicyV2HistoryEventKindSchema.enum;

export const attendancePolicyV2HistoryEventSchema = z.object({
  sessionId: z.string(),
  eventPlainDate: z.instanceof(Temporal.PlainDate),
  kind: attendancePolicyV2HistoryEventKindSchema,
  title: z.string(),
  outcomeLabel: z.string(),
  explanation: z.string().nullable(),
  countsTowardTotalAllowance: z.boolean(),
  countsTowardShortNoticeAllowance: z.boolean(),
  progressionIndex: z.number().int().positive().nullable(),
  progressionOf: z.number().int().positive().nullable(),
});
export type AttendancePolicyV2HistoryEvent = z.infer<typeof attendancePolicyV2HistoryEventSchema>;

export const attendancePolicyV2FutureSubstituteRequestSchema = z.object({
  sessionId: z.string(),
  eventPlainDate: z.instanceof(Temporal.PlainDate),
  kind: z.enum(['regular_sub_request', 'short_notice_sub_request']),
  title: z.string(),
  outcomeLabel: z.string(),
  explanation: z.string().nullable(),
  countsTowardTotalAllowance: z.boolean(),
  countsTowardShortNoticeAllowance: z.boolean(),
});
export type AttendancePolicyV2FutureSubstituteRequest = z.infer<typeof attendancePolicyV2FutureSubstituteRequestSchema>;

export const attendancePolicyV2HistorySchema = z.object({
  studentGroupId: z.string(),
  teacher: z
    .object({
      id: z.string(),
      fullNameOrEmail: z.string(),
    })
    .nullable(),
  overview: attendancePolicyV2AllowanceSchema,
  completedEvents: z.array(attendancePolicyV2HistoryEventSchema),
  activeFutureSubstituteRequests: z.array(attendancePolicyV2FutureSubstituteRequestSchema),
  protection: z.object({
    final14DayActive: z.boolean(),
    blocksReassignment: z.boolean(),
  }),
});
export type AttendancePolicyV2History = z.infer<typeof attendancePolicyV2HistorySchema>;

export const studentInGroupSchema = z.lazy(() =>
  userLikeSchema.extend({
    levelChangeWarning: levelChangeWarningSchema.optional(),
  }),
);
export type StudentInGroup = z.infer<typeof studentInGroupSchema>;

/** Slim reassignment request fields embedded on student-group list/detail payloads. */
export const studentGroupEmbeddedReassignmentRequestSchema = z.object({
  id: z.string(),
  effectivePlainDate: z.instanceof(Temporal.PlainDate),
  displayStatus: z.nativeEnum(PrismaEnums.StudentGroupReassignmentRequestStatus),
});
export type StudentGroupEmbeddedReassignmentRequest = z.infer<typeof studentGroupEmbeddedReassignmentRequestSchema>;

export const studentGroupWithScheduleAndLevelSchema = studentGroupSchema.extend({
  coach: z.lazy(() => userWithCoachProfileSchema.nullable()),
  students: z.array(studentInGroupSchema),
  classTime: z.lazy(() => fullClassTimeSchema),
  learningPath: z.lazy(() => learningPathWithLessonsSchema),
  startingLesson: z.lazy(() => lessonSchema),
  nextLesson: z
    .lazy(() => lessonSchema)
    .nullable()
    .optional(),
  nextSessionAtInstant: z.instanceof(Temporal.Instant).nullable().optional(),
  grades: z.array(z.string()),
  futureSessions: z.number().int().optional(),
  sessionCount: z.number().int(),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  overrideEndPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  coachAssignedAtPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  absenceLimitAnchorPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  coachLastAssignedAtPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  coachLastUnassignedAtPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  studentCount: z.number().int(),
  reassignmentRequests: z.array(studentGroupEmbeddedReassignmentRequestSchema),
  maxPlannedAbsences: z.number().int(),
  shortNoticeSubRequestLimit: z.number().int(),
  attendancePolicyV2: attendancePolicyV2AllowanceSchema.nullable().optional(),
  oneTimeConflicts: z.array(oneTimeConflictSummarySchema).optional(),
  coachAttendanceRollup: getCoachAttendanceRollupResultSchema.optional(),
  attendanceStatus: attendanceStatusSchema.nullable(),
  emailsSent: emailsSentSchema.array(),
  attendanceNotes: z.string().nullable(),
  notes: z.string().nullable(),
  v1AbsenceText: z.string().nullable().optional(),
});

export type StudentGroupWithScheduleAndLevel = z.infer<typeof studentGroupWithScheduleAndLevelSchema>;

export const coachViewableStudentGroupWithScheduleAndLevelSchema = studentGroupWithScheduleAndLevelSchema
  .omit({ students: true })
  .extend({
    students: z.array(coachViewableStudentSchema),
    hasPendingReassignmentRequest: z.boolean(),
    teacherPortalStatus: z.enum(['active', 'removed']),
    teacherPortalLastClassPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
    droppedByTeacher: z.boolean(),
  });
export type CoachViewableStudentGroupWithScheduleAndLevel = z.infer<
  typeof coachViewableStudentGroupWithScheduleAndLevelSchema
>;

export const studentGroupsWithScheduleAndLevelSchema = paginatedResponse.extend({
  studentGroups: z.array(studentGroupWithScheduleAndLevelSchema),
});

export type StudentGroupsWithScheduleAndLevel = z.infer<typeof studentGroupsWithScheduleAndLevelSchema>;

export const getStudentGroupsFilterSchema = z.object({
  query: z.string().optional(),
  scheduleSchools: z.array(z.string()).optional(),
  scheduleOrgs: z.array(z.string()).optional(),
  grades: z.array(studentYearNullableSchema).optional(),
  statuses: z.array(studentGroupStatusSchema).optional(),
  learningPaths: z.array(z.string()).optional(),
  startingLessons: z.array(z.string()).optional(),
  classTimeIds: z.array(z.string()).optional(),
  classTimeTypes: z.array(classTimeTypeSchema).optional(),
  types: z.array(studentGroupTypeSchema).optional(),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  includeLevelChangeWarning: z.boolean().optional(),
  overrideStartPlainDates: z.array(z.instanceof(Temporal.PlainDate)).optional(),
  skipOneTimeConflicts: z.boolean().optional(),
});
export type GetStudentGroupsFilter = z.infer<typeof getStudentGroupsFilterSchema>;

export const getStudentGroupsSortableColumnSchema = z.enum([
  'learningPath',
  'startingLesson',
  'createdAt',
  'updatedAt',
  'nextLesson',
  'futureSessions',
  'studentCount',
  'school',
  'lastManualUpdatedAt',
  'coach',
]);
export type GetStudentGroupsSortableColumn = z.infer<typeof getStudentGroupsSortableColumnSchema>;

export const getStudentGroupsSortOrderSchema = sortOrderSchemaFrom(getStudentGroupsSortableColumnSchema);
export type GetStudentGroupsSortOrder = z.infer<typeof getStudentGroupsSortOrderSchema>;

export const getStudentGroupsModifierSchema = DEPRECATED_pagingSchemaWithMaxPageSize.merge(
  filteringSchemaFrom(getStudentGroupsFilterSchema),
).merge(sortingSchemaFrom(getStudentGroupsSortableColumnSchema));
export type GetStudentGroupsModifier = z.infer<typeof getStudentGroupsModifierSchema>;

export const createStudentGroupOptionsSchema = z.object({
  coachId: z.string().optional(),
  classTimeId: z.string(),
  learningPathId: z.string(),
  startingLessonId: z.string(),
  studentIds: z.array(z.string()),
  status: z.nativeEnum(PrismaEnums.StudentGroupStatus),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  overrideEndPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  v1CoachAssignedAtPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  v1AbsenceText: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type CreateStudentGroupOptions = z.infer<typeof createStudentGroupOptionsSchema>;

export type CreateStudentGroupsCsvResult = CSVUploadResponse;

export const updateStudentGroupOptionsSchema = z.object({
  coachId: z.string().optional(),
  classTimeId: z.string(),
  learningPathId: z.string(),
  startingLessonId: z.string(),
  studentIds: z.array(z.string()),
  status: z.nativeEnum(PrismaEnums.StudentGroupStatus),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  overrideEndPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  v1CoachAssignedAtPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  attendanceStatus: attendanceStatusSchema.nullable(),
  emailsSent: emailsSentSchema.array(),
  coachUnassignedAtPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  attendanceNotes: z.string().nullable(),
  notes: z.string().nullable().optional(),
  v1AbsenceText: z.string().nullable().optional(),
});
export type UpdateStudentGroupOptions = z.infer<typeof updateStudentGroupOptionsSchema>;

export const createStudentGroupCSVRowSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email'),
  learningPathName: z.string().min(1, 'Learning path name is required'),
  startingLessonName: z.string().min(1, 'Starting lesson name is required'),
  classTimeId: z.string().min(1, 'Class time ID is required'),
});

export type CreateStudentGroupCSVRow = z.infer<typeof createStudentGroupCSVRowSchema>;
export const CreateStudentGroupsCSVFormKeys = {
  studentGroupsCSV: 'studentGroupsCSV',
  studentsPerGroup: 'studentsPerGroup',
  schoolId: 'schoolId',
} as const;

export const createStudentGroupsCsvOptionsSchema = z.object({
  [CreateStudentGroupsCSVFormKeys.studentsPerGroup]: z.coerce
    .number()
    .positive({ message: 'Students per group must be positive' })
    .int({ message: 'Students per group must be an integer' }),
  [CreateStudentGroupsCSVFormKeys.schoolId]: z.string({ message: 'School is required' }),
});

export type CreateStudentGroupsCsvOptions = z.infer<typeof createStudentGroupsCsvOptionsSchema>;

// One session line for recreate summary (pre-confirm or post) – canceled sessions
export const recreateSessionSummaryItemSchema = z.object({
  startInstant: z.string(),
  endInstant: z.string(),
  timeZone: z.string(),
  teacherName: z.string().nullable(),
  /** Present when a sub was assigned; null = pending sub teacher */
  subTeacherName: z.string().nullable(),
  /** When true, subTeacherName is the previous sub and they are unavailable at the new time */
  previousSubUnavailable: z.boolean().optional(),
});
export type RecreateSessionSummaryItem = z.infer<typeof recreateSessionSummaryItemSchema>;

// Sub session time changed (API shape with string instants) for pre-confirm summary
export const recreateSubSessionTimeChangedSummarySchema = recreateSessionSummaryItemSchema.extend({
  newStartInstant: z.string(),
  newEndInstant: z.string(),
});
export type RecreateSubSessionTimeChangedSummary = z.infer<typeof recreateSubSessionTimeChangedSummarySchema>;

// Pre-confirm summary for Recreate Sessions modal (one modal: no time change vs time change variant)
export const recreateSessionsSummarySchema = z.object({
  classCount: z.number(),
  canceledCount: z.number(),
  subbedCount: z.number(),
  canceledSessions: z.array(recreateSessionSummaryItemSchema),
  subSessions: z.array(recreateSessionSummaryItemSchema),
  subSessionsTimeChangedCount: z.number().optional(),
  subSessionsTimeChanged: z.array(recreateSubSessionTimeChangedSummarySchema).optional(),
});
export type RecreateSessionsSummary = z.infer<typeof recreateSessionsSummarySchema>;

/**
 * Why a recreate counterpart carries (or does not carry) cancel status (AIR-2311 / AIR-1662).
 * - `original_already_canceled`: source was already cancelled → new session stays cancelled.
 * - `recreate_within_12h_teacher_change`: old row cancelled for ≤12h teacher-change pay protection only
 *   → new session must remain scheduled.
 */
export const recreateSessionCancelReasonSchema = z.enum([
  'original_already_canceled',
  'recreate_within_12h_teacher_change',
]);
export type RecreateSessionCancelReason = z.infer<typeof recreateSessionCancelReasonSchema>;

// Metadata for an archived session used during Recreate Sessions
export const recreateArchivedSessionMetaSchema = z.object({
  status: sessionStatusSchema,
  /** AIR-2311: only `original_already_canceled` should cancel the recreated counterpart. */
  cancelReason: recreateSessionCancelReasonSchema.nullable(),
  oldSessionId: z.string(),
  originalCoachId: z.string().nullable(),
  substituteCoachId: z.string().nullable(),
  acceptedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  createdAtInstant: z.instanceof(Temporal.Instant).nullable(),
  hadConfirmedSubstitute: z.boolean(),
  hadPendingSubstitutionRequest: z.boolean(),
  canceledAtInstant: z.instanceof(Temporal.Instant).nullable(),
  hoursCanceledBeforeStart: z.number().nullable(),
  coachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  addedCoachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  removedCoachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  // Exact OTI snapshot for AIR-2685 "original unchanged" restore (fresh V2 ledger cannot re-derive it).
  originalTeacherIssue: z.nativeEnum(PrismaEnums.OriginalTeacherIssue).nullable(),
  originalTeacherIssueReason: z.string().nullable(),
  originalTeacherIssueReasonDateInstant: z.instanceof(Temporal.Instant).nullable(),
  pinnedSubSession: z.boolean().nullable(),
  startInstant: z.instanceof(Temporal.Instant),
  endInstant: z.instanceof(Temporal.Instant),
  timeZone: z.string(),
  teacherName: z.string().nullable(),
  subTeacherName: z.string().nullable(),
});
export type RecreateArchivedSessionMeta = z.infer<typeof recreateArchivedSessionMetaSchema>;

/**
 * Per–student-group entry produced by the Bulk Action → Update Lesson handler.
 * `sessionsNotUpdated > 0` means the time-window contained more sessions than
 * the learning path had remaining lessons; those exceeding sessions are left
 * unchanged (see AIR-2159).
 */
export const lessonUpdateSummaryItemSchema = z.object({
  classId: z.string(),
  sessionsNotUpdated: z.number().int().nonnegative(),
});
export type LessonUpdateSummaryItem = z.infer<typeof lessonUpdateSummaryItemSchema>;

export const lessonUpdateSummarySchema = z.object({
  fullyUpdated: z.array(lessonUpdateSummaryItemSchema),
  partiallyUpdated: z.array(lessonUpdateSummaryItemSchema),
});
export type LessonUpdateSummary = z.infer<typeof lessonUpdateSummarySchema>;

export const updateStudentGroupBulkActionsResultsSchema = z.object({
  errors: z.array(z.string()).optional(),
  success: z.number(),
  /** Only populated by the Bulk Action → Update Lesson handler. */
  lessonUpdateSummary: lessonUpdateSummarySchema.optional(),
});
export type UpdateStudentGroupBulkActionsResults = z.infer<typeof updateStudentGroupBulkActionsResultsSchema>;

export const bulkSoftDeleteStudentGroupsOptionsSchema = z.object({
  studentGroupIds: z.array(z.string()).min(1),
});
export type BulkSoftDeleteStudentGroupsOptions = z.infer<typeof bulkSoftDeleteStudentGroupsOptionsSchema>;

export const scheduleFromStudentGroupBulkActionsSchema = z.enum(['FromNow', 'WithRange']);
export type ScheduleFromStudentGroupBulkActions = z.infer<typeof scheduleFromStudentGroupBulkActionsSchema>;
export const ScheduleFromStudentGroupBulkActions = scheduleFromStudentGroupBulkActionsSchema.enum;
export const ScheduleFromStudentGroupBulkActionsLabelMap: Record<ScheduleFromStudentGroupBulkActions, string> = {
  [ScheduleFromStudentGroupBulkActions.FromNow]: 'Future sessions from now',
  [ScheduleFromStudentGroupBulkActions.WithRange]: 'Future sessions in range',
};

export const editStudentGroupBulkActionsSchema = z.enum([
  'ExtendLastSession',
  'UpdateTeacher',
  'UpdateLesson',
  'UpdateStudents',
  'RemoveFutureSessions',
  'RecreateSessions',
  'ArchiveSessions',
  'MergeClasses',
  'CancelOneTimeClassFutureSessions',
  'RecreateSessionsForOneTimeClass',
]);
export type EditStudentGroupBulkActions = z.infer<typeof editStudentGroupBulkActionsSchema>;
export const EditStudentGroupBulkActions = editStudentGroupBulkActionsSchema.enum;

export const baseEditStudentGroupsBulkActionsOptionsSchema = z.object({
  studentGroupIds: z.array(z.string()),
  startPlainDate: z.instanceof(Temporal.PlainDate),
  endPlainDate: z.instanceof(Temporal.PlainDate),
  timeZone: z.string(),
});

export const editBulkActionExtendLastSessionSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.ExtendLastSession),
  sendConfirmationEmail: z.boolean().default(false),
});
export type EditBulkActionExtendLastSession = z.infer<typeof editBulkActionExtendLastSessionSchema>;

export const editBulkActionRemoveFutureSessionsSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.RemoveFutureSessions),
  sendConfirmationEmail: z.boolean().default(false),
});
export type EditBulkActionRemoveFutureSessions = z.infer<typeof editBulkActionRemoveFutureSessionsSchema>;

export const editBulkActionUpdateTeacherSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.UpdateTeacher),
  coachId: z.string(),
  reassigned: z.boolean().optional(),
});
export type EditBulkActionUpdateTeacher = z.infer<typeof editBulkActionUpdateTeacherSchema>;

export const editBulkActionUpdateLessonSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.UpdateLesson),
  learningPathId: z.string(),
  startingLessonId: z.string(),
});
export type EditBulkActionUpdateLesson = z.infer<typeof editBulkActionUpdateLessonSchema>;

export const editBulkActionUpdateStudentsSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.UpdateStudents),
  studentIds: z.array(z.string()),
});
export type EditBulkActionUpdateStudents = z.infer<typeof editBulkActionUpdateStudentsSchema>;

export const editBulkActionRecreateSessionsSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.RecreateSessions),
  coachId: z.string().optional(),
  studentIds: z.array(z.string()).optional(),
  learningPathId: z.string().optional(),
  startingLessonId: z.string().optional(),
  scheduleId: z.string().optional(),
  classTimeId: z.string().optional(),
  sendConfirmationEmail: z.boolean().default(false),
});
export type EditBulkActionRecreateSessions = z.infer<typeof editBulkActionRecreateSessionsSchema>;

export const editBulkActionArchiveSessionsSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.ArchiveSessions),
  status: studentGroupStatusSchema,
});
export type EditBulkActionArchiveSessions = z.infer<typeof editBulkActionArchiveSessionsSchema>;

export const editBulkActionMergeClassesSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.MergeClasses),
  coachId: z.string().optional(),
  studentIds: z.array(z.string()).optional(),
  learningPathId: z.string().optional(),
  startingLessonId: z.string().optional(),
});
export type EditBulkActionMergeClasses = z.infer<typeof editBulkActionMergeClassesSchema>;

export const editBulkActionCancelOneTimeClassFutureSessionsSchema =
  baseEditStudentGroupsBulkActionsOptionsSchema.extend({
    bulkType: z.literal(EditStudentGroupBulkActions.CancelOneTimeClassFutureSessions),
  });
export type EditBulkActionCancelOneTimeClassFutureSessions = z.infer<
  typeof editBulkActionCancelOneTimeClassFutureSessionsSchema
>;

export const editBulkActionRecreateSessionsForOneTimeClassSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend(
  {
    bulkType: z.literal(EditStudentGroupBulkActions.RecreateSessionsForOneTimeClass),
  },
);
export type EditBulkActionRecreateSessionsForOneTimeClass = z.infer<
  typeof editBulkActionRecreateSessionsForOneTimeClassSchema
>;

export const editStudentGroupsBulkActionsOptionsSchema = z.discriminatedUnion('bulkType', [
  editBulkActionExtendLastSessionSchema,
  editBulkActionRemoveFutureSessionsSchema,
  editBulkActionUpdateTeacherSchema,
  editBulkActionUpdateLessonSchema,
  editBulkActionUpdateStudentsSchema,
  editBulkActionRecreateSessionsSchema,
  editBulkActionArchiveSessionsSchema,
  editBulkActionMergeClassesSchema,
  editBulkActionCancelOneTimeClassFutureSessionsSchema,
  editBulkActionRecreateSessionsForOneTimeClassSchema,
]);
export type EditStudentGroupsBulkActionsOptions = z.infer<typeof editStudentGroupsBulkActionsOptionsSchema>;

export const editSessionsStudentGroupsBulkActionsSchema = z.object({
  coachId: z.string().optional(),
  studentIds: z.array(z.string()).optional(),
  learningPathId: z.string().optional(),
  startingLessonId: z.string().optional(),
  scheduleId: z.string().optional(),
  classTimeId: z.string().optional(),
  reassigned: z.boolean().optional(),
  timeZone: z.string().optional(),
});
export type EditSessionsStudentGroupsBulkActions = z.infer<typeof editSessionsStudentGroupsBulkActionsSchema>;

export const coachAttendanceSessionsRollupSchema = z.object({
  coachId: z.string(),
  studentGroupIds: z.array(z.string()),
});
export type CoachAttendanceSessionsRollup = z.infer<typeof coachAttendanceSessionsRollupSchema>;

export const getCoachAttendanceRollupOptionsSchema = z.object({
  coachId: z.string(),
  studentGroup: studentGroupWithScheduleAndLevelSchema,
  startOnOrAfter: z.string().optional(),
  endOnOrBefore: z.string().optional(),
});
export type GetCoachAttendanceRollupOptions = z.infer<typeof getCoachAttendanceRollupOptionsSchema>;

export const getStudentGroupsAttendanceSortableColumnSchema = studentGroupWithScheduleAndLevelSchema
  .keyof()
  .extract(['coachAssignedAtPlainDate']);
export type GetStudentGroupsAttendanceSortableColumn = z.infer<typeof getStudentGroupsAttendanceSortableColumnSchema>;

export const getStudentGroupsAttendanceSortOrderSchema = sortOrderSchemaFrom(
  getStudentGroupsAttendanceSortableColumnSchema,
);
export type GetStudentGroupsAttendanceSortOrder = z.infer<typeof getStudentGroupsAttendanceSortOrderSchema>;

export const getStudentGroupsAttendanceFilterSchema = z.object({
  query: z.string().optional(),
  issueDate: z.string().optional(),
  attendanceStatus: z.array(attendanceStatusSchema).optional(),
  actionLevel: z.array(z.nativeEnum(PrismaEnums.TeacherAttendancePolicyActionLevel)).optional(),
  reassignmentDate: z.string().optional(),
});
export type GetStudentGroupsAttendanceFilter = z.infer<typeof getStudentGroupsAttendanceFilterSchema>;

export const getStudentGroupsAttendanceModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getStudentGroupsAttendanceFilterSchema))
  .merge(sortingSchemaFrom(getStudentGroupsAttendanceSortableColumnSchema));
export type GetStudentGroupsAttendanceModifier = z.infer<typeof getStudentGroupsAttendanceModifierSchema>;

export const getStudentGroupsAttendanceResultsSchema = paginatedResponse.extend({
  studentGroups: z.array(studentGroupWithScheduleAndLevelSchema),
});
export type GetStudentGroupsAttendanceResults = z.infer<typeof getStudentGroupsAttendanceResultsSchema>;

export const backfillStudentGroupAuditCoachTransitionDatesOptionsSchema = z.object({
  dryRun: z.boolean().optional(),
  studentGroupIds: z.array(z.string().uuid()).optional(),
});
export type BackfillStudentGroupAuditCoachTransitionDatesOptions = z.infer<
  typeof backfillStudentGroupAuditCoachTransitionDatesOptionsSchema
>;

export const backfillStudentGroupAuditCoachTransitionDatesRowSchema = z.object({
  id: z.string(),
  studentGroupId: z.string(),
  originalCoachId: z.string().nullable(),
  createdAt: z.union([z.string(), z.date()]),
  previousCoachUnassignedAt: z.union([z.string(), z.date()]).nullable(),
  proposedCoachAssignedAt: z.union([z.string(), z.date()]),
});
export type BackfillStudentGroupAuditCoachTransitionDatesRow = z.infer<
  typeof backfillStudentGroupAuditCoachTransitionDatesRowSchema
>;

export const backfillStudentGroupAuditCoachTransitionDatesResultSchema = z.object({
  dryRun: z.boolean(),
  assigned: z.object({
    candidates: z.number(),
    matchedFromPreviousUnassigned: z.number(),
    fallbackToCreatedAt: z.number(),
    updated: z.number(),
  }),
  unassigned: z.object({
    candidates: z.number(),
    updated: z.number(),
  }),
  sampleRows: z.array(backfillStudentGroupAuditCoachTransitionDatesRowSchema),
  affectedStudentGroupIds: z.array(z.string().uuid()),
});
export type BackfillStudentGroupAuditCoachTransitionDatesResult = z.infer<
  typeof backfillStudentGroupAuditCoachTransitionDatesResultSchema
>;

export const backfillStudentGroupAuditCoachTransitionDatesStatsSchema = z.object({
  assignedCandidates: z.number(),
  assignedMatchedFromPreviousUnassigned: z.number(),
  assignedFallbackToCreatedAt: z.number(),
  unassignedCandidates: z.number(),
  affectedStudentGroupIds: z.array(z.string().uuid()),
});
export type BackfillStudentGroupAuditCoachTransitionDatesStats = z.infer<
  typeof backfillStudentGroupAuditCoachTransitionDatesStatsSchema
>;
